import { createFileRoute, useNavigate } from '@tanstack/react-router'
import { useMenuToggle } from '@/features/super-admin/super-admin-shell-context'
import { useState } from 'react'
import { SuperAdminHeader } from '@/features/super-admin/components/SuperAdminHeader'
import { useDashboardMetrics } from '@/features/super-admin/hooks/use-dashboard-metrics'
import { MetricCard } from '@/features/super-admin/components/dashboard/MetricCard'
import { RevenueChart } from '@/features/super-admin/components/dashboard/RevenueChart'
import { SubscriptionFunnel } from '@/features/super-admin/components/dashboard/SubscriptionFunnel'
import { AlertsSummary } from '@/features/super-admin/components/dashboard/AlertsSummary'
import {
  DollarSign,
  TrendingUp,
  Building2,
  Users,
  Activity,
  Target,
  RefreshCw,
} from 'lucide-react'

export const Route = createFileRoute('/super-admin/')({
  component: SuperAdminDashboard,
})

type Period = '7d' | '30d' | '90d' | '12m'

function SuperAdminDashboard() {
  const navigate = useNavigate()
  const { onMenuToggle } = useMenuToggle()
  const [period, setPeriod] = useState<Period>('30d')
  const { data: metrics, isLoading, refetch, isFetching } = useDashboardMetrics({ period })

  const handleFunnelFilterClick = (status: string) => {
    if (status === 'trial_expiring') {
      navigate({ to: '/super-admin/organizations', search: { status: 'trial', trial_expiring_days: 7 } })
    } else {
      navigate({ to: '/super-admin/organizations', search: { status } })
    }
  }

  const periodOptions: { value: Period; label: string }[] = [
    { value: '7d', label: 'Últimos 7 dias' },
    { value: '30d', label: 'Últimos 30 dias' },
    { value: '90d', label: 'Últimos 90 dias' },
    { value: '12m', label: 'Últimos 12 meses' },
  ]

  return (
    <div className="flex flex-col h-full">
      <SuperAdminHeader
        title="Dashboard"
        subtitle="Visão geral da plataforma SecBrain"
        onMenuToggle={onMenuToggle}
        actions={
          <div className="flex items-center gap-3">
            <select
              value={period}
              onChange={(e) => setPeriod(e.target.value as Period)}
              className="px-3 py-2 text-sm bg-white border border-gray-200 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
            >
              {periodOptions.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
            <button
              onClick={() => refetch()}
              disabled={isFetching}
              className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg transition-colors disabled:opacity-50"
              title="Atualizar dados"
            >
              <RefreshCw className={`w-5 h-5 ${isFetching ? 'animate-spin' : ''}`} />
            </button>
          </div>
        }
      />

      <div className="flex-1 p-6 space-y-6 overflow-auto">
        {/* Key Metrics Row */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <MetricCard
            title="MRR (Receita Mensal)"
            value={metrics?.total_mrr || 0}
            change={metrics?.mrr_growth}
            changeLabel="vs período anterior"
            icon={DollarSign}
            iconColor="text-green-600"
            iconBgColor="bg-green-100"
            format="currency"
            loading={isLoading}
          />
          <MetricCard
            title="ARR (Receita Anual)"
            value={metrics?.total_arr || 0}
            icon={TrendingUp}
            iconColor="text-teal-600"
            iconBgColor="bg-teal-100"
            format="currency"
            loading={isLoading}
          />
          <MetricCard
            title="Organizações Ativas"
            value={(metrics?.trial_subscriptions || 0) + (metrics?.active_subscriptions || 0)}
            icon={Building2}
            iconColor="text-blue-600"
            iconBgColor="bg-blue-100"
            loading={isLoading}
          />
          <MetricCard
            title="Taxa de Churn"
            value={metrics?.churn_rate || 0}
            icon={Activity}
            iconColor={
              (metrics?.churn_rate || 0) > 5 ? 'text-red-600' : 'text-green-600'
            }
            iconBgColor={
              (metrics?.churn_rate || 0) > 5 ? 'bg-red-100' : 'bg-green-100'
            }
            format="percent"
            loading={isLoading}
          />
        </div>

        {/* Secondary Metrics Row */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <MetricCard
            title="Total de Organizações"
            value={metrics?.total_organizations || 0}
            icon={Users}
            iconColor="text-purple-600"
            iconBgColor="bg-purple-100"
            loading={isLoading}
          />
          <MetricCard
            title="Em Trial"
            value={metrics?.trial_subscriptions || 0}
            icon={Users}
            iconColor="text-teal-600"
            iconBgColor="bg-teal-100"
            loading={isLoading}
          />
          <MetricCard
            title="Trial Expirando"
            value={metrics?.trial_expiring_soon || 0}
            icon={Target}
            iconColor="text-amber-600"
            iconBgColor="bg-amber-100"
            loading={isLoading}
          />
          <MetricCard
            title="Alertas Ativos"
            value={metrics?.active_alerts || 0}
            icon={Activity}
            iconColor="text-red-600"
            iconBgColor="bg-red-100"
            loading={isLoading}
          />
        </div>

        {/* Charts and Funnel Row */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <RevenueChart period={period} />
          <SubscriptionFunnel
            data={{
              trial: metrics?.trial_subscriptions || 0,
              active: metrics?.active_subscriptions || 0,
              past_due: 0,
              suspended: metrics?.suspended_organizations || 0,
              canceled: metrics?.churned_subscriptions || 0,
              trial_expiring_7d: metrics?.trial_expiring_soon || 0,
            }}
            onFilterClick={handleFunnelFilterClick}
            loading={isLoading}
          />
        </div>

        {/* Alerts and Quick Stats Row */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <div className="lg:col-span-2">
            <AlertsSummary
              data={{
                critical: 0,
                warning: 0,
                info: 0,
                unread: metrics?.active_alerts || 0,
              }}
              loading={isLoading}
            />
          </div>

          {/* Quick Stats Card */}
          <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Resumo do Período</h3>
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-500">Novas organizações</span>
                <span className="text-lg font-semibold text-gray-900">
                  {isLoading ? '...' : metrics?.new_organizations || 0}
                </span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-500">Organizações Ativas</span>
                <span className="text-lg font-semibold text-gray-900">
                  {isLoading ? '...' : metrics?.active_subscriptions || 0}
                </span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-500">Churned no período</span>
                <span className="text-lg font-semibold text-red-600">
                  {isLoading ? '...' : metrics?.churned_subscriptions || 0}
                </span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-500">Suspensas</span>
                <span className="text-lg font-semibold text-amber-600">
                  {isLoading ? '...' : metrics?.suspended_organizations || 0}
                </span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-500">Trial expirando</span>
                <span className="text-lg font-semibold text-orange-600">
                  {isLoading ? '...' : metrics?.trial_expiring_soon || 0}
                </span>
              </div>
            </div>

            <div className="mt-4 pt-4 border-t border-gray-100">
              <p className="text-xs text-gray-400">
                Período: {metrics?.period || '30d'}
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
